// Mapping from application route pathnames to help page deep links.
// If a path is not found in the map, defaultHelpPath is returned.
// NOTE: All keys should be lower-case and represent the pathname portion only (no query/hash).

export const defaultHelpPath = "/helpPage"

// Map of pathname (no trailing slash) to target help route + hash
export const helpSectionMapping: Record<string, string> = {
  "/referrals": "practiceManagement-practiceManagementFaq1",
  "/practiceIndividuals": "practiceManagement-practiceManagementFaq2",
  "/taskboard": "taskboards",
  "/practice": "practiceProfileManagement",
  "/practice/liability": "liabilityInsurance",
  "/provider/liability": "liabilityInsurance",
  "/provider": "providerProfileManagement",
  "/practiceRates": "ratesInvoicing",
  "/payments": "payments",
}

// Normalizes the pathname (remove trailing slash unless it's root)
const normalize = (path: string) => {
  if (!path) return "/"
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1)
  return path
}

/**
 * Given a current app pathname, return the appropriate help page deep link.
 * Falls back to defaultHelpPath when no explicit mapping is defined.
 */
export function getHelpPageForPath(
  pathname: string | undefined | null,
): string {
  const norm = normalize((pathname || "").split("?")[0].split("#")[0])

  // Try exact match first
  let fragment = helpSectionMapping[norm]

  // If no exact match, walk up the path segments until we find a mapped ancestor.
  // Example: /practice/practiceInformation/address -> /practice
  if (!fragment && norm !== "/") {
    const segments = norm.split("/") // leading slash produces first empty element
    // While we have more than one segment beyond the leading empty string
    while (segments.length > 2) {
      segments.pop() // remove deepest segment
      const candidate = segments.join("/") || "/"
      fragment = helpSectionMapping[candidate]
      if (fragment) {
        break
      }
    }
  }

  if (fragment) {
    return `${defaultHelpPath}#${fragment}`
  }
  return defaultHelpPath
}

// Parse hash (#section or #section-subItem) from current location
export function parseHash(hash: string): { section?: string; anchor?: string } {
  if (!hash) return {}
  const trimmed = hash.replace(/^#/, "")
  // Accept patterns like 'section', 'section-sub', or '/helpPage#section-sub' already given by router
  const parts = trimmed.split("-")
  if (parts.length === 1) return { section: parts[0] }
  return { section: parts[0], anchor: parts.slice(1).join("-") }
}
